import ExcelJS from "exceljs";

/**
 * Generate Excel report for warranty records with multiple sheets
 * @param {Array} warranties - Array of warranty objects
 * @param {string} propertyName - Name of the property
 */
export const generateWarrantyReportExcel = async (warranties, propertyName) => {
  const workbook = new ExcelJS.Workbook();

  workbook.creator = "Warranty Tracker System";
  workbook.created = new Date();
  workbook.modified = new Date();

  // Separate warranties by status
  const allWarranties = warranties;
  const activeWarranties = warranties.filter((w) => w.daysRemaining > 30);
  const expiringWarranties = warranties.filter((w) => w.daysRemaining >= 0 && w.daysRemaining <= 30);
  const expiredWarranties = warranties.filter((w) => w.daysRemaining < 0);

  // Define column structure
  const columns = [
    { header: "Serial Number", key: "serialNumber", width: 20 },
    { header: "Item Name", key: "itemName", width: 30 },
    { header: "Item Type", key: "itemType", width: 20 },
    { header: "Vendor", key: "vendor", width: 20 },
    { header: "PO Number", key: "poNumber", width: 15 },
    { header: "Purchase Date", key: "purchaseDate", width: 15 },
    { header: "Purchase Cost", key: "purchaseCost", width: 15 },
    { header: "Warranty Type", key: "warrantyTypeName", width: 25 },
    { header: "Warranty Start", key: "warrantyStartDate", width: 15 },
    { header: "Warranty End", key: "warrantyEndDate", width: 15 },
    { header: "Days Remaining", key: "daysRemaining", width: 15 },
    { header: "Status", key: "statusLabel", width: 15 },
    { header: "Location", key: "location", width: 20 },
    { header: "Notes", key: "notes", width: 40 },
  ];

  // Helper function to create and format a worksheet
  const createSheet = (sheetName, data, tabColor) => {
    const sheet = workbook.addWorksheet(sheetName);
    sheet.columns = columns;

    // Header row formatting
    sheet.getRow(1).font = { bold: true, size: 11 };
    sheet.getRow(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2980B9" }, // Blue header
    };
    sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
    sheet.getRow(1).alignment = { vertical: "middle", horizontal: "center" };
    sheet.getRow(1).height = 20;

    // Set tab color
    if (tabColor) {
      sheet.properties.tabColor = { argb: tabColor };
    }

    // Add data rows
    data.forEach((warranty) => {
      const row = sheet.addRow({
        serialNumber: warranty.serialNumber || "",
        itemName: warranty.itemName || "",
        itemType: warranty.itemType || "",
        vendor: warranty.vendor || "",
        poNumber: warranty.poNumber || "",
        purchaseDate: warranty.purchaseDate || "",
        purchaseCost: warranty.purchaseCost || 0,
        warrantyTypeName: warranty.warrantyTypeName || "Custom",
        warrantyStartDate: warranty.warrantyStartDate || "",
        warrantyEndDate: warranty.warrantyEndDate || "",
        daysRemaining: warranty.daysRemaining,
        statusLabel: warranty.statusLabel || "",
        location: warranty.location || "",
        notes: warranty.notes || "",
      });

      // Format purchase cost as currency
      row.getCell("purchaseCost").numFmt = "$#,##0.00";

      // Conditional formatting for Days Remaining column
      const daysCell = row.getCell("daysRemaining");
      if (warranty.daysRemaining < 0) {
        daysCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF44336" }, // Red
        };
        daysCell.font = { color: { argb: "FFFFFFFF" }, bold: true };
      } else if (warranty.daysRemaining <= 30) {
        daysCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFF9800" }, // Orange
        };
        daysCell.font = { color: { argb: "FFFFFFFF" }, bold: true };
      } else if (warranty.daysRemaining <= 90) {
        daysCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFC107" }, // Yellow/Amber
        };
      } else {
        daysCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4CAF50" }, // Green
        };
        daysCell.font = { color: { argb: "FFFFFFFF" }, bold: true };
      }

      // Conditional formatting for Status column
      const statusCell = row.getCell("statusLabel");
      if (warranty.statusLabel === "Expired") {
        statusCell.font = { color: { argb: "FFF44336" }, bold: true };
      } else if (warranty.statusLabel === "Expiring Soon") {
        statusCell.font = { color: { argb: "FFFF9800" }, bold: true };
      } else {
        statusCell.font = { color: { argb: "FF4CAF50" } };
      }
    });

    // Freeze header row
    sheet.views = [{ state: "frozen", ySplit: 1 }];

    // Add auto-filter to all columns
    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: columns.length },
    };

    // Add summary at the bottom
    const summaryRowNum = sheet.rowCount + 2;
    sheet.getRow(summaryRowNum).font = { bold: true };
    sheet.getCell(`A${summaryRowNum}`).value = "Total Records:";
    sheet.getCell(`B${summaryRowNum}`).value = data.length;

    return sheet;
  };

  // Create sheets
  createSheet("All Warranties", allWarranties, "FF808080"); // Gray
  createSheet("Active", activeWarranties, "FF4CAF50"); // Green
  createSheet("Expiring Soon", expiringWarranties, "FFFF9800"); // Orange
  createSheet("Expired", expiredWarranties, "FFF44336"); // Red

  // Create Summary sheet
  const summarySheet = workbook.addWorksheet("Summary");
  summarySheet.properties.tabColor = { argb: "FF2980B9" }; // Blue

  // Summary title
  summarySheet.getCell("A1").value = "Warranty Summary Report";
  summarySheet.getCell("A1").font = { bold: true, size: 16 };
  summarySheet.getCell("A2").value = `Property: ${propertyName}`;
  summarySheet.getCell("A2").font = { bold: true, size: 12 };
  summarySheet.getCell("A3").value = `Generated: ${new Date().toLocaleString()}`;
  summarySheet.getCell("A3").font = { size: 10, italic: true };

  // Statistics table
  summarySheet.getCell("A5").value = "Status";
  summarySheet.getCell("B5").value = "Count";
  summarySheet.getRow(5).font = { bold: true };
  summarySheet.getRow(5).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF2980B9" },
  };
  summarySheet.getRow(5).font = { bold: true, color: { argb: "FFFFFFFF" } };

  summarySheet.getCell("A6").value = "Total Warranties";
  summarySheet.getCell("B6").value = allWarranties.length;

  summarySheet.getCell("A7").value = "Active (>30 days)";
  summarySheet.getCell("B7").value = activeWarranties.length;
  summarySheet.getCell("B7").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF4CAF50" },
  };

  summarySheet.getCell("A8").value = "Expiring Soon (0-30 days)";
  summarySheet.getCell("B8").value = expiringWarranties.length;
  summarySheet.getCell("B8").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFF9800" },
  };

  summarySheet.getCell("A9").value = "Expired";
  summarySheet.getCell("B9").value = expiredWarranties.length;
  summarySheet.getCell("B9").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF44336" },
  };

  summarySheet.getColumn("A").width = 30;
  summarySheet.getColumn("B").width = 15;

  // Breakdown by Item Type
  const itemTypeBreakdown = {};
  allWarranties.forEach((w) => {
    const type = w.itemType || "Unknown";
    if (!itemTypeBreakdown[type]) {
      itemTypeBreakdown[type] = { total: 0, active: 0, expiring: 0, expired: 0 };
    }
    itemTypeBreakdown[type].total++;
    if (w.daysRemaining > 30) itemTypeBreakdown[type].active++;
    else if (w.daysRemaining >= 0 && w.daysRemaining <= 30) itemTypeBreakdown[type].expiring++;
    else itemTypeBreakdown[type].expired++;
  });

  let currentRow = 11;
  summarySheet.getCell(`A${currentRow}`).value = "Breakdown by Item Type";
  summarySheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
  currentRow += 1;

  summarySheet.getCell(`A${currentRow}`).value = "Item Type";
  summarySheet.getCell(`B${currentRow}`).value = "Total";
  summarySheet.getCell(`C${currentRow}`).value = "Active";
  summarySheet.getCell(`D${currentRow}`).value = "Expiring";
  summarySheet.getCell(`E${currentRow}`).value = "Expired";
  summarySheet.getRow(currentRow).font = { bold: true };
  summarySheet.getRow(currentRow).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD3D3D3" },
  };

  Object.entries(itemTypeBreakdown)
    .sort((a, b) => b[1].total - a[1].total)
    .forEach(([type, counts]) => {
      currentRow++;
      summarySheet.getCell(`A${currentRow}`).value = type;
      summarySheet.getCell(`B${currentRow}`).value = counts.total;
      summarySheet.getCell(`C${currentRow}`).value = counts.active;
      summarySheet.getCell(`D${currentRow}`).value = counts.expiring;
      summarySheet.getCell(`E${currentRow}`).value = counts.expired;
    });

  summarySheet.getColumn("C").width = 12;
  summarySheet.getColumn("D").width = 12;
  summarySheet.getColumn("E").width = 12;

  // Download the file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `warranty-report-${propertyName.replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);

  return a.download;
};
